import { useMutation } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { hydraUiApiUs } from '@/services/api';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { currentOfferingsByPlanType, PlanType } from '@/services/billing/plans';

/**
 * The support inbox a contracts-sales request routes to: Builder accounts go to CJS, trials to sales,
 * current-offering plans to premium, and everything else (legacy plans) to upgrades.
 */
export type ContractsSalesInbox = 'cjs' | 'sales' | 'premium' | 'upgrades';

/** The form fields submitted with a contracts-sales request. */
export interface ContractsSalesSubmission {
  message: string;
  features: string;
  replyTo: string;
}

function deriveInbox(isBuilder: boolean, isTrial: boolean, planType: PlanType | undefined): ContractsSalesInbox {
  if (isBuilder) {
    return 'cjs';
  }
  if (isTrial) {
    return 'sales';
  }
  if (currentOfferingsByPlanType.includes(planType ?? PlanType.NotReportable)) {
    return 'premium';
  }
  return 'upgrades';
}

/**
 * Submits a contracts-sales ("Let's talk") request to the support inbox that matches the account's
 * plan.
 *
 * Pinned to the **US** data center. A regional host would need its own `support_tickets` consumer for
 * the lead to reach anyone, and a dropped sales lead fails silently — so every inbox goes to one place.
 * The copilot account-management tool posts to the same premium inbox and is pinned with it.
 *
 * **Known gap:** `support.create` (help/support form), `support.feedback` and `support.sign` still post
 * through the account-regional client. The same argument applies to them, but they belong to other
 * features and were left for a follow-up rather than changed from a billing PR.
 *
 * `releaseDate` has no value to send, and the backend marks it optional
 * (`services/ui_api/support/types.go`), so it stays empty.
 */
export function useSubmitContractsSales() {
  const { account } = useAuth();
  const isBuilder = useStore(billing.isBuilder);
  const isTrial = useStore(billing.isTrial);
  const planType = useStore(billing.planType);

  return useMutation({
    mutationFn: async (submission: ContractsSalesSubmission) => {
      const inbox = deriveInbox(isBuilder, isTrial, planType);
      const params = { path: { account_id: String(account.id) } };
      const body = {
        message: submission.message,
        features: submission.features,
        replyTo: submission.replyTo,
        consent_to_access: false,
        source: 'Available Plans',
        browser: window.navigator.userAgent,
        url: window.location.href,
        windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
        releaseDate: '',
        // So the support ticket records which build the request came from. Same value Sentry reports.
        version: __HYDRA__APP__VERSION__,
      };

      switch (inbox) {
        case 'cjs':
          return hydraUiApiUs.support.cjs({ params, body });
        case 'sales':
          return hydraUiApiUs.support.sales({ params, body });
        case 'premium':
          return hydraUiApiUs.support.premium({ params, body });
        case 'upgrades':
          return hydraUiApiUs.support.upgrades({ params, body });
        default: {
          const _exhaustive: never = inbox;
          throw new Error(`Unhandled contracts-sales inbox: ${String(_exhaustive)}`);
        }
      }
    },
  });
}
